import get from "lodash.get";
import AreaUtils from "services/areas/utils";
import MapApi from "services/map/api";
import MapUtils from "services/map/utils";
import ContractUtils from "services/contract/utils";
import PedestrianStatisticsApi from "./api";
import PedestrianStatisticsUtils from "./utils";

import PedestrianStatisticsActionTypes from "./types";

class PedestrianStatisticsActioner {
  static setQueueDailyWaitingTimeStats(stats) {
    return {
      type: PedestrianStatisticsActionTypes.SET_QUEUE_DAILY_WAITING_TIME_STATS,
      stats,
    };
  }

  static setQueueDailyAttendanceMaxStats(stats) {
    return {
      type: PedestrianStatisticsActionTypes.SET_QUEUE_DAILY_ATTENDANCE_MAX_STATS,
      stats,
    };
  }

  static setQueueDailyAttendanceStats(stats) {
    return {
      type: PedestrianStatisticsActionTypes.SET_QUEUE_DAILY_ATTENDANCE_STATS,
      stats,
    };
  }

  static setQueueDetailsStats(stats) {
    return {
      type: PedestrianStatisticsActionTypes.SET_QUEUE_DETAILS_STATS,
      stats,
    };
  }

  static setQueueDailyWaitingTimeStatsError(error) {
    return {
      type: PedestrianStatisticsActionTypes.SET_QUEUE_DAILY_WAITING_TIME_STATS_ERROR,
      error,
    };
  }

  static setQueueDailyAttendanceMaxStatsError(error) {
    return {
      type: PedestrianStatisticsActionTypes.SET_QUEUE_DAILY_ATTENDANCE_MAX_STATS_ERROR,
      error,
    };
  }

  static setQueueDailyAttendanceStatsError(error) {
    return {
      type: PedestrianStatisticsActionTypes.SET_QUEUE_DAILY_ATTENDANCE_STATS_ERROR,
      error,
    };
  }

  static setQueueDetailsStatsError(error) {
    return {
      type: PedestrianStatisticsActionTypes.SET_QUEUE_DETAILS_STATS_ERROR,
      error,
    };
  }

  static setGeneralStats(stats) {
    return {
      type: PedestrianStatisticsActionTypes.SET_GENERAL_STATS,
      stats,
    };
  }

  static setDetailsStats(stats) {
    return {
      type: PedestrianStatisticsActionTypes.SET_DETAILS_STATS,
      stats,
    };
  }

  static setFilters(data) {
    return {
      type: PedestrianStatisticsActionTypes.SET_FILTERS,
      data,
    };
  }

  static setFilter(key, value) {
    return {
      type: PedestrianStatisticsActionTypes.SET_FILTER,
      key,
      value,
    };
  }

  static setDailyAttendanceStats(stats) {
    return {
      type: PedestrianStatisticsActionTypes.SET_DAILY_ATTENDANCE_STATS,
      stats,
    };
  }

  static setConfig(config) {
    return {
      type: PedestrianStatisticsActionTypes.SET_CONFIG,
      config,
    };
  }

  static errorOnRetrieve(error) {
    return {
      type: PedestrianStatisticsActionTypes.ERROR_ON_RETRIEVE,
      error,
    };
  }

  static removeError() {
    return {
      type: PedestrianStatisticsActionTypes.REMOVE_ERROR,
    };
  }

  static setLoading(statName) {
    return {
      type: PedestrianStatisticsActionTypes.SET_LOADING,
      statName,
    };
  }

  static setQueueLoading(statName) {
    return {
      type: PedestrianStatisticsActionTypes.SET_QUEUE_LOADING,
      statName,
    };
  }

  static setAutoRefresh(autoRefresh) {
    return {
      type: PedestrianStatisticsActionTypes.SET_AUTO_REFRESH,
      autoRefresh,
    };
  }

  static setInitialized(value) {
    return {
      type: PedestrianStatisticsActionTypes.SET_INITIALIZED,
      value,
    };
  }

  static setMap(map) {
    return {
      type: PedestrianStatisticsActionTypes.SET_MAP_INFOS,
      map,
    };
  }

  static setSelectedLayers(layers) {
    return {
      type: PedestrianStatisticsActionTypes.SET_SELECTED_LAYERS,
      layers,
    };
  }

  static retrieveLayersInfos() {
    return (dispatch, getState) => {
      const PedestrianStatisticsState = getState().PedestrianStatistics;
      if (!get(PedestrianStatisticsState, "autoRefresh", false)) {
        dispatch(PedestrianStatisticsActioner.setLoading("details"));
      }

      const contractId = getState().Contract.contract.id;
      return MapApi.getAreasInfos({
        contract_id: contractId,
        types: ["QUEUEMANAGEMENT", "PEDESTRIANANDFLOW", "UNEQUIPPED"],
      })
        .then((layersInfos) => {
          const mapParkingConfig = get(getState(), "Map.config.pedestrian", {});

          if (Object.values(mapParkingConfig).length === 0) {
            return Promise.resolve();
          }

          AreaUtils.sortAreasByConfigMapAreasOrder(layersInfos.areas, [
            ...(mapParkingConfig?.areas ?? []),
            ...(mapParkingConfig?.queue_areas ?? []),
          ]);

          const formattedMapInfos =
            PedestrianStatisticsUtils.formatLayersInfosFromApi(
              layersInfos,
              mapParkingConfig
            );
          const currentSelectedLayers = get(
            getState(),
            "PedestrianStatistics.map.selectedLayers"
          );
          dispatch(PedestrianStatisticsActioner.setMap(formattedMapInfos));
          dispatch(
            PedestrianStatisticsActioner.setSelectedLayers(
              currentSelectedLayers ||
                PedestrianStatisticsUtils.getDefaultSelectedLayers(
                  formattedMapInfos
                )
            )
          );
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }

  static retrieveAllStats = () => {
    return (dispatch, getState) => {
      const { map, filters } = getState().PedestrianStatistics;
      const { contract } = getState().Contract;

      if (
        map.initialized &&
        MapUtils.toggleMarkersAndAreas(
          filters?.timestamp,
          ContractUtils.getPedestrianMaxDaysAllowed(contract)
        )
      ) {
        Promise.all([
          dispatch(PedestrianStatisticsActioner.retrieveGeneralStats("areas")),
          dispatch(
            PedestrianStatisticsActioner.retrieveDailyAttendanceStats("areas")
          ),
          dispatch(PedestrianStatisticsActioner.retrieveDetailsStats("areas")),
          dispatch(PedestrianStatisticsActioner.retrieveQueueDetailsStats()),
          dispatch(
            PedestrianStatisticsActioner.retrieveQueueDailyAttendanceMaxStats()
          ),
          dispatch(
            PedestrianStatisticsActioner.retrieveQueueDailyAttendanceStats()
          ),
          dispatch(
            PedestrianStatisticsActioner.retrieveQueueDailyWaitingTimeStats()
          ),
        ]).then(() => {
          dispatch(PedestrianStatisticsActioner.setInitialized(true));
        });
      }
    };
  };

  // Check if some required filters are missing
  static missingRequiredFilters(type = "default", filters) {
    if (
      type === "areas" &&
      (!get(filters, "areas") ||
        !get(filters, "timestamp") ||
        get(filters, "timestamp") < 0)
    ) {
      return true;
    }

    return false;
  }

  static retrieveQueueDailyWaitingTimeStats() {
    return (dispatch, getState) => {
      const selectedQueueAreas = getState()
        .PedestrianStatistics.map.selectedLayers.map((selectedArea) =>
          selectedArea.apiType === "QUEUEMANAGEMENT" ? selectedArea : null
        )
        .filter((area) => area !== null);

      if (selectedQueueAreas.length === 0) {
        return Promise.resolve([]);
      }

      dispatch(
        PedestrianStatisticsActioner.setQueueLoading("dailyWaitingTime")
      );

      const promises = selectedQueueAreas.map((area) => {
        return PedestrianStatisticsApi.getQueueWaitingTimeStats({
          timestamp: getState().PedestrianStatistics?.filters.timestamp,
          areas: area.area_id,
          x_axis: getState().PedestrianStatistics?.filters
            .isPedestrianQueueDailyWaitingTimeChartTimeRangeByHour
            ? "HOUR"
            : "5MIN",
        }).then((stats) => {
          return { [area.area_id]: stats };
        });
      });

      return Promise.all(promises)
        .then((data) => {
          dispatch(
            PedestrianStatisticsActioner.setQueueDailyWaitingTimeStats(data)
          );
        })
        .catch((error) => {
          dispatch(
            PedestrianStatisticsActioner.setQueueDailyWaitingTimeStatsError(
              error.status === 403 ? "Unauthorized" : "Unknown error"
            )
          );
        });
    };
  }

  static retrieveQueueDailyAttendanceMaxStats() {
    return (dispatch, getState) => {
      const selectedQueueAreas = getState()
        .PedestrianStatistics.map.selectedLayers.map((selectedArea) =>
          selectedArea.apiType === "QUEUEMANAGEMENT" ? selectedArea : null
        )
        .filter((area) => area !== null);

      if (selectedQueueAreas.length === 0) {
        return Promise.resolve([]);
      }

      dispatch(
        PedestrianStatisticsActioner.setQueueLoading("dailyAttendanceMax")
      );

      const promises = selectedQueueAreas.map(async (area) => {
        // try {
        const stats =
          await PedestrianStatisticsApi.getQueueDailyAttendanceMaxStats({
            timestamp: getState().PedestrianStatistics?.filters.timestamp,
            areas: area.area_id,
            x_axis: getState().PedestrianStatistics?.filters
              .isPedestrianQueueAffluenceMaxChartTimeRangeByHour
              ? "HOUR"
              : "5MIN",
          });
        return { [area.area_id]: stats };
        // } catch (error) {
        //   dispatch(
        //     PedestrianStatisticsActioner.setQueueDailyAttendanceMaxStats({
        //       error,
        //     })
        //   );
        // }
      });

      return Promise.all(promises)
        .then((data) => {
          dispatch(
            PedestrianStatisticsActioner.setQueueDailyAttendanceMaxStats(data)
          );
        })
        .catch((error) => {
          dispatch(
            PedestrianStatisticsActioner.setQueueDailyAttendanceMaxStatsError(
              error.status === 403 ? "Unauthorized" : "Unknown error"
            )
          );
        });
    };
  }

  static retrieveQueueDailyAttendanceStats() {
    return (dispatch, getState) => {
      const selectedQueueAreas = getState()
        .PedestrianStatistics.map.selectedLayers.map((selectedArea) =>
          selectedArea.apiType === "QUEUEMANAGEMENT" ? selectedArea : null
        )
        .filter((area) => area !== null);

      if (selectedQueueAreas.length === 0) {
        return Promise.resolve([]);
      }

      dispatch(PedestrianStatisticsActioner.setQueueLoading("dailyAttendance"));

      const promises = selectedQueueAreas.map(async (area) => {
        // try {
        const stats =
          await PedestrianStatisticsApi.getQueueDailyAttendanceStats({
            timestamp: getState().PedestrianStatistics?.filters.timestamp,
            areas: area.area_id,
            x_axis: getState().PedestrianStatistics?.filters
              .isPedestrianQueueAffluenceChartTimeRangeByHour
              ? "HOUR"
              : "5MIN",
          });
        return { [area.area_id]: stats };
        // } catch (error) {
        //   dispatch(
        //     PedestrianStatisticsActioner.setQueueDailyAttendanceStats({
        //       error,
        //     })
        //   );
        // }
      });

      return Promise.all(promises)
        .then((data) => {
          dispatch(
            PedestrianStatisticsActioner.setQueueDailyAttendanceStats(data)
          );
        })
        .catch((error) => {
          dispatch(
            PedestrianStatisticsActioner.setQueueDailyAttendanceStatsError(
              error.status === 403 ? "Unauthorized" : "Unknown error"
            )
          );
        });
    };
  }

  static retrieveQueueDetailsStats() {
    return (dispatch, getState) => {
      const selectedQueueAreas = getState()
        .PedestrianStatistics.map.selectedLayers.map((selectedArea) =>
          selectedArea.apiType === "QUEUEMANAGEMENT" ? selectedArea : null
        )
        .filter((area) => area !== null);

      if (selectedQueueAreas.length === 0) {
        return Promise.resolve([]);
      }

      return PedestrianStatisticsApi.getQueueDetailsStats({
        timestamp: getState().PedestrianStatistics?.filters.timestamp,
        areas: selectedQueueAreas.map((area) => area.area_id),
      })
        .then((stats) =>
          dispatch(PedestrianStatisticsActioner.setQueueDetailsStats(stats))
        )
        .catch((error) => {
          dispatch(
            PedestrianStatisticsActioner.setQueueDetailsStatsError(
              error.status === 403 ? "Unauthorized" : "Unknown error"
            )
          );
        });
    };
  }

  static retrieveGeneralStats(type = "areas") {
    return (dispatch, getState) => {
      let filters = PedestrianStatisticsUtils.getFilters(type, getState());
      const PedestrianStatisticsState = getState().PedestrianStatistics;

      // Don't refresh if any areas selected
      if (PedestrianStatisticsActioner.missingRequiredFilters(type, filters)) {
        return Promise.resolve([]);
      }

      if (!get(PedestrianStatisticsState, "autoRefresh", false)) {
        dispatch(PedestrianStatisticsActioner.setLoading("general"));
      }

      if (type === "radius") {
        const contractId = getState().Contract.contract.id;
        filters = {
          ...filters,
          contract_id: contractId,
        };
      }

      return PedestrianStatisticsApi.getGeneralStats(type, filters)
        .then((stats) => {
          dispatch(PedestrianStatisticsActioner.setGeneralStats(stats));

          if (stats.timestamp) {
            dispatch(
              PedestrianStatisticsActioner.setFilter(
                "timestamp",
                stats.timestamp
              )
            );
          }
        })
        .catch((error) => {
          dispatch(PedestrianStatisticsActioner.setGeneralStats({ error }));
        });
    };
  }

  static retrieveDetailsStats(type = "areas") {
    return (dispatch, getState) => {
      let filters = PedestrianStatisticsUtils.getFilters(
        `${type}Details`,
        getState()
      );

      // Don't refresh if any areas selected
      if (PedestrianStatisticsActioner.missingRequiredFilters(type, filters)) {
        return Promise.resolve([]);
      }

      return PedestrianStatisticsApi.getDetailsStats(type, filters).then(
        (stats) => {
          const formattedStats =
            PedestrianStatisticsUtils.formatDetailsStatsFromApi(stats);
          dispatch(
            PedestrianStatisticsActioner.setDetailsStats(formattedStats)
          );
        }
      );
    };
  }

  static retrieveDailyAttendanceStats(type = "areas") {
    return (dispatch, getState) => {
      const selectedAreas = getState().PedestrianStatistics.map.selectedLayers;

      // Don't refresh if no pedestrian and flow areas are selected
      if (selectedAreas.every((area) => area.apiType !== "PEDESTRIANANDFLOW")) {
        return Promise.resolve([]);
      }

      let filters = PedestrianStatisticsUtils.getFilters(type, getState());
      if (!filters.areas) {
        return Promise.resolve([]);
      }

      const areasData = getState().PedestrianStatistics.map.areas;
      filters.areas = filters.areas.filter((area) => {
        return (
          areasData.find((a) => a.id === area)?.apiType === "PEDESTRIANANDFLOW"
        );
      });

      // Don't refresh if any areas selected
      if (PedestrianStatisticsActioner.missingRequiredFilters(type, filters)) {
        return Promise.resolve([]);
      }

      const PedestrianStatisticsState = getState().PedestrianStatistics;
      if (!get(PedestrianStatisticsState, "autoRefresh", false)) {
        dispatch(PedestrianStatisticsActioner.setLoading("dailyAttendance"));
      }

      if (type === "radius") {
        const contractId = getState().Contract.contract.id;
        filters = {
          ...filters,
          contract_id: contractId,
        };
      }

      return PedestrianStatisticsApi.getDailyAttendanceStats(type, filters)
        .then((stats) => {
          dispatch(PedestrianStatisticsActioner.setDailyAttendanceStats(stats));

          if (stats.timestamp) {
            dispatch(
              PedestrianStatisticsActioner.setFilter(
                "timestamp",
                stats.timestamp
              )
            );
          }
        })
        .catch((error) => {
          dispatch(
            PedestrianStatisticsActioner.setDailyAttendanceStats({ error })
          );
        });
    };
  }
}

export default PedestrianStatisticsActioner;
